import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { getLanguages } from "../api";

const fallbackLanguages = [
  { code: "en", name: "English", native_name: "English", is_default: true, enabled: true },
  { code: "es", name: "Spanish", native_name: "Espanol", is_default: false, enabled: true },
  { code: "de", name: "German", native_name: "Deutsch", is_default: false, enabled: true },
];

const dictionary = {
  en: {},
  es: {
    language_manage_title: "Gestion de idiomas",
  },
  de: {
    language_manage_title: "Sprachverwaltung",
  },
};

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [languages, setLanguages] = useState(fallbackLanguages);
  const [languageCode, setLanguageCode] = useState("en");

  useEffect(() => {
    const load = async () => {
      try {
        const languagesRes = await getLanguages();

        if (Array.isArray(languagesRes.data)) {
          const enabledRows = languagesRes.data.filter((row) => row.enabled !== false);
          if (enabledRows.length) {
            setLanguages(enabledRows);
            const defaultLanguage =
              enabledRows.find((row) => row.is_default) ||
              enabledRows[0];
            setLanguageCode(String(defaultLanguage.code || "en").toLowerCase());
            return;
          }
        }
      } catch {
        const defaultLanguage =
          fallbackLanguages.find((row) => row.is_default) ||
          fallbackLanguages[0];
        setLanguageCode(String(defaultLanguage.code || "en").toLowerCase());
      }
    };

    load();
  }, []);

  const setLanguage = useCallback(() => {
    // Language selection is controlled centrally via admin-managed configuration.
  }, []);

  const t = useMemo(() => {
    const activeDictionary = dictionary[languageCode] || dictionary.en || {};
    return (key, fallback = "") => activeDictionary[key] || fallback || key;
  }, [languageCode]);

  const value = useMemo(
    () => ({
      languageCode,
      languages,
      setLanguage,
      t,
    }),
    [languageCode, languages, t]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }
  return context;
}
